/**
 * 🗂️ AI Plan Modu Servisi — gts_ai_api /api/AiPlan istemcisi.
 *
 * Plan Modu (AI Pro): çok adımlı görev planları (örn. şube açılışı) sunucuda saklanır,
 * görevler panel üzerinden ajan sohbetine gönderilir. Bu servis saf CRUD'dur;
 * görevlerin AI'ya gönderimi AiChatWindow içindeki plan çalıştırıcısında yapılır.
 */
import { aiApi } from '../config/apiClient';

// ============================================
// TYPES
// ============================================

export type AiPlanTaskStatus = 'pending' | 'running' | 'done' | 'failed' | 'needsReview';
export type AiPlanStatus = 'active' | 'completed' | 'archived';

/** Plan listesi satırı (panel liste görünümü). */
export interface AiPlanSummary {
  id: number;
  title: string;
  templateKey: string | null;
  status: AiPlanStatus;
  taskCount: number;
  doneCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Plan görevi. */
export interface AiPlanTaskDto {
  id: number;
  sortOrder: number;
  title: string;
  /** {degisken} yer tutuculu görev komutu — çalıştırılırken contextVars ile doldurulur. */
  promptTemplate: string;
  status: AiPlanTaskStatus;
  resultSummary: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

/** Plan detayı (görevleriyle). */
export interface AiPlanDetail {
  id: number;
  title: string;
  templateKey: string | null;
  /** Plan değişkenleri (ContextJson parse edilmiş hali; örn. { branchName: 'Kadıköy' }). */
  contextVars: Record<string, string>;
  conversationId: number | null;
  status: AiPlanStatus;
  createdAt: string;
  updatedAt: string;
  tasks: AiPlanTaskDto[];
}

export interface AiPlanCreateTaskInput {
  title: string;
  promptTemplate: string;
  sortOrder: number;
}

export interface AiPlanCreateInput {
  title: string;
  templateKey?: string | null;
  contextVars?: Record<string, string>;
  tasks: AiPlanCreateTaskInput[];
}

/** Şirketin plan modu paketi kapalıysa fırlatılan hatanın kodu. */
export const PLAN_DISABLED_CODE = 'PLAN_DISABLED';

/**
 * Plan verisi değişince yayınlanan pencere olayı — AI eylemleri (createAiPlan vb.)
 * bunu tetikler, AiChatWindow dinleyip plan panelini tazeler.
 */
export const AI_PLAN_CHANGED_EVENT = 'gts:aiPlanChanged';

/** Plan değişikliğini duyurur; openPanel=true ise panel otomatik açılır. */
export const notifyAiPlanChanged = (openPanel = false): void => {
  window.dispatchEvent(new CustomEvent(AI_PLAN_CHANGED_EVENT, { detail: { openPanel } }));
};

export class AiPlanServiceError extends Error {
  code: string | null;

  constructor(message: string, code: string | null = null) {
    super(message);
    this.code = code;
  }
}

// ============================================
// MAPPERS (PascalCase → camelCase)
// ============================================

const parseContextVars = (json: string | null | undefined): Record<string, string> => {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const vars: Record<string, string> = {};
      Object.entries(parsed).forEach(([k, v]) => {
        if (v !== null && v !== undefined) vars[k] = String(v);
      });
      return vars;
    }
  } catch {
    /* bozuk ContextJson — boş bağlamla devam */
  }
  return {};
};

const VALID_TASK_STATUSES: AiPlanTaskStatus[] = ['pending', 'running', 'done', 'failed', 'needsReview'];

const mapTaskStatus = (s: any): AiPlanTaskStatus =>
  VALID_TASK_STATUSES.includes(s) ? s : 'pending';

const mapPlanStatus = (s: any): AiPlanStatus =>
  s === 'completed' || s === 'archived' ? s : 'active';

const mapPlanSummary = (p: any): AiPlanSummary => ({
  id: p.Id ?? 0,
  title: p.Title ?? '',
  templateKey: p.TemplateKey ?? null,
  status: mapPlanStatus(p.Status),
  taskCount: p.TaskCount ?? 0,
  doneCount: p.DoneCount ?? 0,
  createdAt: p.CreatedAt ?? '',
  updatedAt: p.UpdatedAt ?? '',
});

const mapPlanTask = (t: any): AiPlanTaskDto => ({
  id: t.Id ?? 0,
  sortOrder: t.SortOrder ?? 0,
  title: t.Title ?? '',
  promptTemplate: t.PromptTemplate ?? '',
  status: mapTaskStatus(t.Status),
  resultSummary: t.ResultSummary ?? null,
  startedAt: t.StartedAt ?? null,
  completedAt: t.CompletedAt ?? null,
});

const mapPlanDetail = (p: any): AiPlanDetail => ({
  id: p.Id ?? 0,
  title: p.Title ?? '',
  templateKey: p.TemplateKey ?? null,
  contextVars: parseContextVars(p.ContextJson),
  conversationId: p.ConversationId ?? null,
  status: mapPlanStatus(p.Status),
  createdAt: p.CreatedAt ?? '',
  updatedAt: p.UpdatedAt ?? '',
  tasks: (p.Tasks || []).map(mapPlanTask),
});

/** Yanıt zarfını doğrular; Success=false ise kodlu hata fırlatır (PLAN_DISABLED dahil). */
const unwrap = (d: any): any => {
  if (d?.Success !== true) {
    throw new AiPlanServiceError(d?.Message || 'AI plan isteği başarısız.', d?.Code ?? null);
  }
  return d;
};

// ============================================
// SERVICE
// ============================================

class AiPlanService {
  /** Kullanıcının plan özetleri (son 50, en yeni üstte). */
  async getPlans(userId: string): Promise<AiPlanSummary[]> {
    const response = await aiApi.get<any>(
      `/api/AiPlan/plans?userId=${encodeURIComponent(userId)}`
    );
    const d = unwrap(response.data);
    return (d.Data || []).map(mapPlanSummary);
  }

  /** Plan detayı + görevler (SortOrder sıralı). */
  async getPlan(planId: number, userId: string): Promise<AiPlanDetail> {
    const response = await aiApi.get<any>(
      `/api/AiPlan/plans/${planId}?userId=${encodeURIComponent(userId)}`
    );
    const d = unwrap(response.data);
    return mapPlanDetail(d.Data || {});
  }

  /** Yeni plan + görevleri oluşturur; oluşan planın detayını döner. */
  async createPlan(input: AiPlanCreateInput, userId: string): Promise<AiPlanDetail> {
    const response = await aiApi.post<any>('/api/AiPlan/create', {
      title: input.title,
      templateKey: input.templateKey ?? null,
      contextJson:
        input.contextVars && Object.keys(input.contextVars).length > 0
          ? JSON.stringify(input.contextVars)
          : null,
      userId,
      tasks: input.tasks.map((t) => ({
        title: t.title,
        promptTemplate: t.promptTemplate,
        sortOrder: t.sortOrder,
      })),
    });
    const d = unwrap(response.data);
    return mapPlanDetail(d.Data || {});
  }

  /**
   * Plan kısmi güncelleme (başlık / bağlam / conversationId / status / görev listesi).
   * tasks verilirse görev listesi TAMAMEN yeniden yazılır: aynı başlıklı görevler
   * durumunu koruyarak güncellenir, listede olmayanlar silinir, yeniler pending eklenir.
   */
  async updatePlan(
    planId: number,
    userId: string,
    changes: {
      title?: string;
      contextVars?: Record<string, string>;
      conversationId?: number;
      clearConversation?: boolean;
      status?: AiPlanStatus;
      tasks?: AiPlanCreateTaskInput[];
    }
  ): Promise<void> {
    const response = await aiApi.post<any>('/api/AiPlan/update', {
      planId,
      userId,
      title: changes.title ?? null,
      contextJson: changes.contextVars ? JSON.stringify(changes.contextVars) : null,
      conversationId: changes.conversationId ?? null,
      clearConversation: changes.clearConversation === true,
      status: changes.status ?? null,
      tasks:
        changes.tasks && changes.tasks.length > 0
          ? changes.tasks.map((t) => ({
              title: t.title,
              promptTemplate: t.promptTemplate,
              sortOrder: t.sortOrder,
            }))
          : null,
    });
    unwrap(response.data);
  }

  /** Görev durumu güncelleme — her durum geçişi anında sunucuya yazılır (cihazdan bağımsız devam). */
  async updateTask(
    taskId: number,
    userId: string,
    status: AiPlanTaskStatus,
    resultSummary?: string | null
  ): Promise<void> {
    const response = await aiApi.post<any>('/api/AiPlan/task-update', {
      taskId,
      userId,
      status,
      resultSummary: resultSummary ?? null,
    });
    unwrap(response.data);
  }

  /** Planı siler (soft delete). */
  async deletePlan(planId: number, userId: string): Promise<void> {
    const response = await aiApi.post<any>('/api/AiPlan/delete', { planId, userId });
    unwrap(response.data);
  }

  /**
   * Planı imzalı dosya içeriği (.gtsplan.json) olarak dışa aktarır. İmza sunucuda
   * üretilir; içerik olduğu gibi dosyaya yazılmalıdır (alan değişirse imza bozulur).
   */
  async exportPlan(planId: number, userId: string, exportTitle?: string): Promise<Record<string, any>> {
    // exportTitle: dosyadaki plan başlığı bu olur (imza sunucuda bu ada göre üretilir)
    const response = await aiApi.post<any>('/api/AiPlan/export', {
      planId,
      userId,
      exportTitle: exportTitle?.trim() || null,
    });
    const d = unwrap(response.data);
    return d.Data || {};
  }

  /**
   * İmzalı plan dosyasını yeni plan olarak içe aktarır (tüm görevler pending).
   * İmza doğrulanamazsa INVALID_PLAN_FILE kodlu hata fırlatılır — elle yazılmış
   * dosyalar kabul edilmez. Şirketten bağımsızdır (başka şirketin dosyası açılabilir).
   */
  async importPlan(planFile: Record<string, any>, userId: string): Promise<AiPlanDetail> {
    const response = await aiApi.post<any>('/api/AiPlan/import', { userId, plan: planFile });
    const d = unwrap(response.data);
    return mapPlanDetail(d.Data || {});
  }
}

const aiPlanService = new AiPlanService();
export default aiPlanService;
